import React, { useState } from 'react';
import { Paper, Button, Box, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { format } from 'date-fns';
import { DealType, DealStage } from '../../../../utils/enums'
import useApiManager from '../../../../utils/Api/apiManager';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmationDialog from '../../../ConfirmationDialog/ConfirmationDialog';
import { useNavigate } from 'react-router-dom';

const DeleteRow = ({ hover }) => (
    <Box sx={{ position: 'relative', width: '20px', height: '22px' }}>
        {hover && <DeleteIcon />}
    </Box>
);

const Deals = ({ data, fetchDeals, contactId }) => {
    const navigate = useNavigate();
    const { del } = useApiManager();
    const [hoveredRow, setHoveredRow] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentRow, setCurrentRow] = useState('');

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleConfirmAction = () => {
        handleDelete();
        handleCloseDialog();
    };

    const handleDelete = async () => {
        try {
            await del(`/deals/${currentRow.id}`);
            fetchDeals();
        } catch (error) {
            console.error('Error deleting deal:', error);
        } 
    };

    const handleNewDeal = () => {
        navigate('/deals/create', { state: { contactData: { contactId: contactId } } });
    };

    return (
        <Paper elevation={0} sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px' }}>
                <h4>Deals</h4>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" onClick={handleNewDeal}>New Deal</Button>
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell width={10}>-</TableCell>
                            <TableCell width={100}>Deal Name</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="right">Stage</TableCell>
                            <TableCell align="right">Probability (%)</TableCell>
                            <TableCell align="right">Closing Date</TableCell>
                            <TableCell align="right">Type</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.map((row) => (
                            <TableRow
                                key={row.id}
                                onMouseEnter={() => setHoveredRow(row.id)}
                                onMouseLeave={() => setHoveredRow(null)}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <Tooltip title="Delete">
                                    <TableCell sx={{ width: '20px', height: '22px', padding: '8px', cursor: 'pointer' }} 
                                    onClick={() => {
                                        setCurrentRow(row);
                                        handleOpenDialog();
                                        }}>
                                        <DeleteRow hover={hoveredRow === row.id} />
                                    </TableCell>
                                </Tooltip>
                                <TableCell component="th" scope="row">
                                <Link to={`/deals/${row.id}`}>{row.dealName}</Link>
                                </TableCell>
                                <TableCell align="right">{row.amount}</TableCell>
                                <TableCell align="right">{DealStage?.[row.stage]?.label || 'Unknown Stage'}</TableCell>
                                <TableCell align="right">{row.probability}</TableCell>
                                <TableCell align="right">
                                {row.closingDate ? format(new Date(row.closingDate), 'MMMM do yyyy') : 'No closing date'}
                                </TableCell>
                                <TableCell align="right">
                                {DealType?.[row.type]?.label || 'Unknown Type'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <ConfirmationDialog
                open={openDialog}
                title="Confirm Action"
                content="Are you sure you want to delete this deal? This action cannot be undone!"
                onClose={handleCloseDialog}
                onConfirm={handleConfirmAction}
                confirmText="Confirm"
                cancelText="Cancel"
            />
        </Paper>
    );
};

export default Deals;
